import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, MapPin, Share2, ArrowRight, ArrowUpRight, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MT_KEY = process.env.REACT_APP_MAPTILER_KEY;

const T = {
  cream: "#FBF5EA", card: "#FFFFFF", ink: "#141414", muted: "#6B6B6B", line: "#E9E2D4",
  green: "#4E9A5A", greenSoft: "#E6F2E7", yellow: "#F5C842", yellowSoft: "#FFF4CC", orange: "#EE7A3B",
};
const fHead = '"Inter Tight",system-ui,sans-serif';

const media = (ref) => {
  if (!ref) return "";
  if (/^https?:\/\//.test(ref) || ref.startsWith("/")) return ref;
  return `${API}/files/${ref}`;
};
const photoSrc = (p) => media(p?.url || p?.file_id || "");

const MODE_LABEL = {
  it: { piedi: "A piedi", moto: "In moto", bici: "In bici", auto: "In macchina", barca: "In barca" },
  en: { piedi: "On foot", moto: "By motorbike", bici: "By bike", auto: "By car", barca: "By boat" },
  fr: { piedi: "À pied", moto: "En moto", bici: "À vélo", auto: "En voiture", barca: "En bateau" },
  de: { piedi: "Zu Fuß", moto: "Mit dem Motorrad", bici: "Mit dem Rad", auto: "Mit dem Auto", barca: "Mit dem Boot" },
  es: { piedi: "A pie", moto: "En moto", bici: "En bici", auto: "En coche", barca: "En barco" },
};
const L10N = {
  it: { title: "Tour consigliati", filterAll: "Tutte", discover: "Scopri tutto", takeMe: "Portami alla partenza",
    none: "Nessun tour di questo tipo, per ora.", duration: "Durata", away: "Percorso", gain: "Salita", speed: "Velocità media",
    effort: "Fatica", route: "Il percorso", routeHint: "tocca i punti sulla mappa", steps: "Passo dopo passo",
    photos: "Le foto", scroll: "scorri", tip: "Il consiglio di", eat: "Dove mangiare", zone: "in zona", takeThere: "Portami lì",
    bring: "Cosa portare", links: "Link utili", share: "Condividi", aiNote: "Testi creati con l'aiuto dell'AI e verificati da",
    updated: "aggiornato", lRoute: "Percorso", lPark: "Parcheggi", lEat: "Mangiare", lBoat: "Imbarco", example: "Esempio",
    exampleLine: "Questo è un esempio: crea i tuoi tour dall'admin", recommended: "CONSIGLIATO" },
  en: { title: "Recommended tours", filterAll: "All", discover: "Discover", takeMe: "Take me to the start",
    none: "No tours of this type yet.", duration: "Duration", away: "Route", gain: "Ascent", speed: "Avg speed",
    effort: "Effort", route: "The route", routeHint: "tap the points on the map", steps: "Step by step",
    photos: "Photos", scroll: "swipe", tip: "Tip from", eat: "Where to eat", zone: "nearby", takeThere: "Take me there",
    bring: "What to bring", links: "Useful links", share: "Share", aiNote: "Texts created with AI and verified by",
    updated: "updated", lRoute: "Route", lPark: "Parking", lEat: "Eat", lBoat: "Boarding", example: "Example",
    exampleLine: "This is an example: create your own tours from the admin", recommended: "RECOMMENDED" },
  fr: { title: "Tours conseillés", filterAll: "Tous", discover: "Découvrir", takeMe: "M'emmener au départ",
    none: "Aucun tour de ce type pour l'instant.", duration: "Durée", away: "Parcours", gain: "Dénivelé", speed: "Vitesse moy.",
    effort: "Difficulté", route: "Le parcours", routeHint: "touchez les points", steps: "Étape par étape",
    photos: "Photos", scroll: "glissez", tip: "Le conseil de", eat: "Où manger", zone: "à proximité", takeThere: "M'y emmener",
    bring: "À emporter", links: "Liens utiles", share: "Partager", aiNote: "Textes créés avec l'IA et vérifiés par",
    updated: "mis à jour", lRoute: "Parcours", lPark: "Parking", lEat: "Manger", lBoat: "Embarquement", example: "Exemple",
    exampleLine: "Ceci est un exemple : créez vos tours dans l'admin", recommended: "CONSEILLÉ" },
  de: { title: "Empfohlene Touren", filterAll: "Alle", discover: "Entdecken", takeMe: "Zum Start bringen",
    none: "Noch keine Touren dieser Art.", duration: "Dauer", away: "Strecke", gain: "Anstieg", speed: "Ø Tempo",
    effort: "Schwierigkeit", route: "Die Route", routeHint: "Punkte antippen", steps: "Schritt für Schritt",
    photos: "Fotos", scroll: "wischen", tip: "Tipp von", eat: "Wo essen", zone: "in der Nähe", takeThere: "Hinbringen",
    bring: "Mitnehmen", links: "Nützliche Links", share: "Teilen", aiNote: "Texte mit KI erstellt und geprüft von",
    updated: "aktualisiert", lRoute: "Route", lPark: "Parken", lEat: "Essen", lBoat: "Einstieg", example: "Beispiel",
    exampleLine: "Dies ist ein Beispiel: erstelle deine Touren im Admin", recommended: "EMPFOHLEN" },
  es: { title: "Tours recomendados", filterAll: "Todos", discover: "Descubrir", takeMe: "Llévame al inicio",
    none: "Aún no hay tours de este tipo.", duration: "Duración", away: "Recorrido", gain: "Desnivel", speed: "Vel. media",
    effort: "Dificultad", route: "El recorrido", routeHint: "toca los puntos", steps: "Paso a paso",
    photos: "Fotos", scroll: "desliza", tip: "El consejo de", eat: "Dónde comer", zone: "cerca", takeThere: "Llévame allí",
    bring: "Qué llevar", links: "Enlaces útiles", share: "Compartir", aiNote: "Textos creados con IA y verificados por",
    updated: "actualizado", lRoute: "Recorrido", lPark: "Parking", lEat: "Comer", lBoat: "Embarque", example: "Ejemplo",
    exampleLine: "Esto es un ejemplo: crea tus tours desde el admin", recommended: "RECOMENDADO" },
};
const TL = (lang) => L10N[lang] || L10N.it;
const difLabel = { it: { facile: "Facile", media: "Media", impegnativa: "Impegnativa" },
  en: { facile: "Easy", media: "Medium", impegnativa: "Hard" }, fr: { facile: "Facile", media: "Moyen", impegnativa: "Difficile" },
  de: { facile: "Leicht", media: "Mittel", impegnativa: "Schwer" }, es: { facile: "Fácil", media: "Media", impegnativa: "Difícil" } };

const ModeIcon = ({ mode, size = 16, color = "currentColor" }) => {
  const p = { fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    piedi: <><circle cx="13" cy="4" r="2" /><path d="M10 21l2-6 3 3v3M8 12l2-4 4 1 2 3 3 1M10 8l-2 6" /></>,
    moto: <><circle cx="5" cy="17" r="3" /><circle cx="19" cy="17" r="3" /><path d="M5 17l4-6h5l3 6M14 11l-2-4h3M9 11h-3" /></>,
    bici: <><circle cx="5.5" cy="17" r="3.5" /><circle cx="18.5" cy="17" r="3.5" /><path d="M5.5 17l4-8h6l3 8M9.5 9l3 8M14 6h3" /></>,
    auto: <><path d="M3 16v-4l2-5h14l2 5v4z" /><circle cx="7" cy="16" r="2" /><circle cx="17" cy="16" r="2" /><path d="M3 12h18" /></>,
    barca: <><path d="M3 17l2 3h14l2-3zM12 3v12M12 4l6 9h-6" /><path d="M2 21c2 1 4 1 6 0s4-1 6 0 4 1 6 0" /></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" {...p}>{paths[mode] || paths.piedi}</svg>;
};

const fmtDur = (min) => {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h ? `${h}h${m ? " " + String(m).padStart(2, "0") : ""}` : `${m} min`;
};

// ─── Leaflet helpers ───
const pinIcon = (label, bg, fg) => L.divIcon({
  className: "", iconSize: [34, 42], iconAnchor: [17, 42],
  html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${bg};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);font:800 13px ${fHead};color:${fg}">${label}</span></div>`,
});
const dotIcon = (n) => L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;border-radius:50%;background:${T.yellow};border:2px solid ${T.ink};display:flex;align-items:center;justify-content:center;font:800 11px ${fHead};color:${T.ink}">${n}</div>`,
});
const poiIcon = (emoji) => L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 13],
  html: `<div style="width:26px;height:26px;border-radius:50%;background:#fff;border:2px solid ${T.orange};display:flex;align-items:center;justify-content:center;font-size:13px">${emoji}</div>` });

function FitRoute({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords && coords.length) map.fitBounds(coords, { padding: [30, 30] });
  }, [coords, map]);
  return null;
}
function DrawRoute({ coords }) {
  const ref = useRef();
  useEffect(() => {
    const layer = ref.current;
    if (!layer || !layer._path) return;
    const path = layer._path;
    try {
      const len = path.getTotalLength();
      path.style.transition = "none";
      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = len;
      // force reflow then animate
      void path.getBoundingClientRect();
      path.style.transition = "stroke-dashoffset 1.8s ease-out";
      path.style.strokeDashoffset = "0";
    } catch (e) { /* noop */ }
  }, [coords]);
  return <Polyline ref={ref} positions={coords} pathOptions={{ color: T.ink, weight: 4, lineCap: "round" }} />;
}

export default function TourSection({ propertyId, lang = "it", hostName = "" }) {
  const t = TL(lang);
  const [tours, setTours] = useState([]);
  const [mode, setMode] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    axios.get(`${API}/welcomebook/${propertyId}/tours?lang=${lang}`)
      .then((r) => setTours(r.data.tours || []))
      .catch(() => setTours([]));
  }, [propertyId, lang]);

  // open from #tour-{id}
  useEffect(() => {
    const h = window.location.hash;
    if (h && h.startsWith("#tour-") && tours.length) {
      const id = h.slice(6);
      const found = tours.find((x) => x.tour_id === id);
      if (found) setOpen(found);
    }
  }, [tours]);

  const modes = useMemo(() => Array.from(new Set(tours.map((x) => x.mode))), [tours]);
  const list = tours.filter((x) => mode === "all" || x.mode === mode);
  if (!tours.length) return null;

  const chip = (active) => ({
    flex: "none", border: "none", borderRadius: 99, padding: "9px 15px", cursor: "pointer",
    font: `700 14px ${fHead}`, display: "flex", gap: 6, alignItems: "center",
    background: active ? T.ink : "#fff", color: active ? "#fff" : T.ink, boxShadow: active ? "none" : `0 1px 0 ${T.line}`,
  });

  return (
    <div data-testid="wb3-tours-section" style={{ marginTop: 10 }}>
      <h2 className="disp" style={{ font: `800 30px/1.05 ${fHead}`, letterSpacing: "-.02em", margin: "0 0 4px" }}>{t.title}</h2>
      <p style={{ margin: "0 0 12px", color: T.muted, fontSize: 14 }}>
        {lang === "it" ? `Gite consigliate da ${hostName || "noi"} che non puoi perderti.` : `${t.title} — ${hostName || ""}`.trim()}
      </p>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12, scrollbarWidth: "none" }}>
        <button onClick={() => setMode("all")} style={chip(mode === "all")} data-testid="wb3-tour-chip-all">{t.filterAll}</button>
        {modes.map((m) => (
          <button key={m} onClick={() => setMode(m)} style={chip(mode === m)} data-testid={`wb3-tour-chip-${m}`}>
            <ModeIcon mode={m} /> {(MODE_LABEL[lang] || MODE_LABEL.it)[m]}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 18, scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
        {list.length === 0 && <p style={{ color: T.muted, fontSize: 14 }}>{t.none}</p>}
        {list.map((x) => {
          const cover = x.photos && x.photos.length ? photoSrc(x.photos[x.cover_index || 0] || x.photos[0]) : "";
          const cat = `${(MODE_LABEL[lang] || MODE_LABEL.it)[x.mode]} · ${fmtDur(x.duration_min)}`;
          return (
            <article key={x.tour_id} style={{ flex: "none", width: 286, background: T.card, borderRadius: 26, overflow: "hidden", scrollSnapAlign: "start", boxShadow: `0 2px 0 ${T.line}`, display: "flex", flexDirection: "column" }} data-testid={`wb3-tour-card-${x.tour_id}`}>
              <div style={{ height: 178, position: "relative", background: "#DDE6EC" }}>
                {cover && <img src={cover} alt={x.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                <span style={{ position: "absolute", top: 14, left: 14, background: T.yellow, borderRadius: 99, padding: "6px 12px", font: `800 11px ${fHead}`, letterSpacing: ".1em" }}>★ {t.recommended}</span>
                {x.is_demo && <span style={{ position: "absolute", bottom: 14, left: 14, background: T.ink, color: "#fff", borderRadius: 99, padding: "5px 11px", font: `800 10px ${fHead}`, letterSpacing: ".1em" }}>{t.example.toUpperCase()}</span>}
                {x.distance_km != null && <span style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,.92)", borderRadius: 99, padding: "6px 12px", font: `800 11px ${fHead}` }}>{x.distance_km} km</span>}
              </div>
              <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                <span style={{ font: `800 12px ${fHead}`, letterSpacing: ".12em", textTransform: "uppercase", color: T.green }}>{cat}</span>
                <h3 className="disp" style={{ font: `800 22px/1.1 ${fHead}`, margin: 0 }}>{x.title}</h3>
                <p style={{ margin: 0, color: T.muted, fontSize: 14, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{x.description}</p>
                <div style={{ display: "grid", gap: 8, marginTop: "auto", paddingTop: 10 }}>
                  <button onClick={() => { setOpen(x); window.history.replaceState(null, "", `#tour-${x.tour_id}`); }} data-testid={`wb3-tour-open-${x.tour_id}`} style={{ border: "none", borderRadius: 16, padding: "13px 16px", font: `700 15px ${fHead}`, background: T.green, color: "#fff", display: "flex", gap: 8, justifyContent: "center", alignItems: "center", cursor: "pointer" }}>→ {t.discover}</button>
                  <a href={startMaps(x)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", borderRadius: 16, padding: "13px 16px", font: `700 15px ${fHead}`, background: T.ink, color: "#fff", display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}><MapPin size={17} style={{ color: T.yellow }} />{t.takeMe}</a>
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {x_example(list, t)}
      {open && <TourModal tour={open} lang={lang} hostName={hostName} onClose={() => { setOpen(null); window.history.replaceState(null, "", window.location.pathname + window.location.search); }} />}
    </div>
  );
}

const x_example = (list, t) => {
  return null;
};

const startMaps = (x) => {
  const s = x.start || {};
  if (s.lat && s.lng) return `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.label || x.title)}`;
};

function TourModal({ tour, lang, hostName, onClose }) {
  const t = TL(lang);
  const [layers, setLayers] = useState({ route: true, park: true, eat: true, boat: true });
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onPop = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("popstate", onPop); document.body.style.overflow = ""; };
  }, [onClose]);

  const cover = tour.photos && tour.photos.length ? photoSrc(tour.photos[tour.cover_index || 0] || tour.photos[0]) : "";
  const isSpeed = tour.mode === "moto" || tour.mode === "auto";
  const speed = (tour.distance_km && tour.duration_min) ? Math.round(tour.distance_km / (tour.duration_min / 60)) + " km/h" : "—";
  const geo = tour.route_geojson;
  const coords = geo && geo.coordinates ? geo.coordinates.map((c) => [c[1], c[0]]) : [];
  const A = tour.start || {}, B = tour.end || {};
  const stepPins = (tour.steps || []).map((s, i) => (s.lat && s.lng ? { i: i + 1, pos: [s.lat, s.lng] } : null)).filter(Boolean);
  const parkPois = (tour.pois || []).filter((p) => p.type === "parcheggio" && p.lat && p.lng);
  const eatPois = (tour.pois || []).filter((p) => p.type === "mangiare" && p.lat && p.lng);
  const boatPois = (tour.pois || []).filter((p) => p.type === "imbarco" && p.lat && p.lng);
  const now = new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { month: "long", year: "numeric" });

  const h4 = { font: `800 21px/1.1 ${fHead}`, margin: "0 0 12px", display: "flex", justifyContent: "space-between", alignItems: "baseline" };
  const block = { margin: "26px 16px 0" };
  const lchip = (on) => ({ flex: "none", border: "none", borderRadius: 99, padding: "7px 12px", font: `700 12.5px ${fHead}`, background: on ? T.ink : T.cream, color: on ? "#fff" : T.ink, cursor: "pointer" });

  return (
    <div data-testid="wb3-tour-modal" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(20,20,20,.4)", zIndex: 4000, display: "flex", alignItems: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", height: "calc(100% - 38px)", background: T.cream, borderRadius: "30px 30px 0 0", overflow: "hidden", animation: "wbTourUp .45s cubic-bezier(.2,.9,.25,1)" }}>
        <style>{`@keyframes wbTourUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
        <span style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: 44, height: 5, borderRadius: 9, background: "rgba(255,255,255,.85)", zIndex: 5 }} />
        <button onClick={onClose} data-testid="wb3-tour-close" aria-label="Chiudi" style={{ position: "absolute", top: 14, right: 14, zIndex: 6, width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.95)", display: "grid", placeItems: "center", cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,.18)" }}><X size={18} color={T.ink} /></button>

        <div style={{ position: "absolute", inset: 0, overflowY: "auto", paddingBottom: 110, scrollbarWidth: "none", zIndex: 1 }}>
          {/* hero */}
          <div style={{ height: 300, position: "relative", background: "#CBD8DF" }}>
            {cover && <img src={cover} alt={tour.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            <div style={{ position: "absolute", inset: "auto 0 0 0", height: 120, background: `linear-gradient(transparent, ${T.cream})` }} />
            <span style={{ position: "absolute", top: 18, left: 16, background: T.yellow, borderRadius: 99, padding: "6px 12px", font: `800 11px ${fHead}`, letterSpacing: ".1em" }}>★ {t.recommended}</span>
          </div>
          {/* head */}
          <div style={{ textAlign: "center", padding: "0 22px", marginTop: -58, position: "relative" }}>
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", background: T.greenSoft, color: T.green, padding: "5px 12px", borderRadius: 99, font: `800 12px ${fHead}` }}><ModeIcon mode={tour.mode} color={T.green} /> {(MODE_LABEL[lang] || MODE_LABEL.it)[tour.mode]}{tour.subtitle ? ` · ${tour.subtitle}` : ""}</span>
            <h2 className="disp" style={{ font: `900 32px/1.02 ${fHead}`, letterSpacing: "-.025em", margin: "10px 0 8px" }}>{tour.title}</h2>
            {tour.description && <p style={{ margin: "0 auto", color: T.muted, fontSize: 15, maxWidth: "30ch" }}>{tour.description}</p>}
          </div>
          {/* stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, margin: "18px 16px 0" }}>
            {[[fmtDur(tour.duration_min), t.duration], [`${tour.distance_km ?? "—"} km`, t.away],
              isSpeed ? [speed, t.speed] : [`${tour.elevation_gain_m ?? 0} m`, t.gain],
              [(difLabel[lang] || difLabel.it)[tour.difficulty] || tour.difficulty, t.effort]].map((s, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 18, padding: "10px 4px", textAlign: "center", display: "grid", gap: 2 }}>
                <b style={{ font: `800 17px/1.1 ${fHead}` }}>{s[0]}</b>
                <small style={{ font: `600 10px ${fHead}`, letterSpacing: ".06em", textTransform: "uppercase", color: T.muted }}>{s[1]}</small>
              </div>
            ))}
          </div>
          {/* A -> B */}
          <div style={{ margin: "14px 16px 0", background: T.ink, color: "#fff", borderRadius: 24, padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10, alignItems: "center" }}>
              <div><span style={{ width: 30, height: 30, borderRadius: "50%", background: T.green, color: "#fff", display: "grid", placeItems: "center", font: `900 14px ${fHead}` }}>A</span></div>
              <div style={{ textAlign: "center", fontSize: 18, lineHeight: 1 }}><ModeIcon mode={tour.mode} size={20} color="#fff" /></div>
              <div style={{ textAlign: "right" }}><span style={{ width: 30, height: 30, borderRadius: "50%", background: T.yellow, color: T.ink, display: "grid", placeItems: "center", font: `900 14px ${fHead}`, marginLeft: "auto" }}>B</span></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
              <div><b style={{ font: `800 15px ${fHead}` }}>{A.label || A.address}</b></div>
              <div style={{ textAlign: "right" }}><b style={{ font: `800 15px ${fHead}` }}>{B.label || B.address}</b></div>
            </div>
            {tour.return_mode && <div style={{ borderTop: "1px solid #333", paddingTop: 10, marginTop: 10, fontSize: 12.5, color: "#D6D6D6" }}>⟲ {retLabel(tour.return_mode, lang)}</div>}
          </div>

          {/* mappa */}
          {coords.length > 1 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.route} <small style={{ font: `600 12px ${fHead}`, color: T.muted }}>{t.routeHint}</small></h4>
              <div style={{ background: "#fff", borderRadius: 24, overflow: "hidden" }}>
                <div style={{ height: 260 }} data-testid="wb3-tour-map">
                  <MapContainer center={coords[0]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} attributionControl={false}>
                    <TileLayer url={`https://api.maptiler.com/maps/landscape/{z}/{x}/{y}.png?key=${MT_KEY}`} />
                    <FitRoute coords={coords} />
                    <Polyline positions={coords} pathOptions={{ color: "#fff", weight: 8, lineCap: "round" }} />
                    {layers.route && <DrawRoute coords={coords} />}
                    <Marker position={coords[0]} icon={pinIcon("A", T.green, "#fff")} />
                    <Marker position={coords[coords.length - 1]} icon={pinIcon("B", T.yellow, T.ink)} />
                    {stepPins.map((s) => <Marker key={s.i} position={s.pos} icon={dotIcon(s.i)} />)}
                    {layers.park && parkPois.map((p, i) => <Marker key={"pk" + i} position={[p.lat, p.lng]} icon={poiIcon("🅿")} />)}
                    {layers.eat && eatPois.map((p, i) => <Marker key={"ea" + i} position={[p.lat, p.lng]} icon={poiIcon("🍝")} />)}
                    {layers.boat && boatPois.map((p, i) => <Marker key={"bo" + i} position={[p.lat, p.lng]} icon={poiIcon("⛴")} />)}
                  </MapContainer>
                </div>
                <div style={{ display: "flex", gap: 6, padding: 10, overflowX: "auto" }}>
                  <button style={lchip(layers.route)} onClick={() => setLayers((s) => ({ ...s, route: !s.route }))}>{t.lRoute}</button>
                  {parkPois.length > 0 && <button style={lchip(layers.park)} onClick={() => setLayers((s) => ({ ...s, park: !s.park }))}>🅿 {t.lPark}</button>}
                  {eatPois.length > 0 && <button style={lchip(layers.eat)} onClick={() => setLayers((s) => ({ ...s, eat: !s.eat }))}>🍝 {t.lEat}</button>}
                  {boatPois.length > 0 && <button style={lchip(layers.boat)} onClick={() => setLayers((s) => ({ ...s, boat: !s.boat }))}>⛴ {t.lBoat}</button>}
                </div>
              </div>
            </div>
          )}

          {/* passo dopo passo */}
          {(tour.steps || []).length > 0 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.steps} <small style={{ font: `600 12px ${fHead}`, color: T.muted }}>{tour.steps.length} {lang === "it" ? "tappe" : ""}</small></h4>
              <div>
                {tour.steps.map((s, i) => {
                  const hl = i === 0 || i === tour.steps.length - 1;
                  return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 1fr", gap: 12, position: "relative", paddingBottom: 16 }}>
                      {i < tour.steps.length - 1 && <span style={{ position: "absolute", left: 19, top: 40, bottom: 0, borderLeft: "2px dashed #CFC6B3" }} />}
                      <span style={{ width: 40, height: 40, borderRadius: 14, background: hl ? T.yellow : "#fff", display: "grid", placeItems: "center", font: `900 15px ${fHead}`, boxShadow: `0 1px 0 ${T.line}` }}>{i + 1}</span>
                      <div>
                        <p className="disp" style={{ font: `800 16px/1.2 ${fHead}`, margin: "2px 0 3px" }}>{s.title}</p>
                        <p style={{ fontSize: 14, color: "#4A4A4A", margin: 0 }}>{s.text}</p>
                        {s.tag && <span style={{ display: "inline-block", marginTop: 6, font: `700 11px ${fHead}`, letterSpacing: ".06em", background: "#fff", borderRadius: 99, padding: "3px 9px", color: T.muted }}>{s.tag}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* foto */}
          {(tour.photos || []).length > 0 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.photos} <small style={{ font: `600 12px ${fHead}`, color: T.muted }}>{t.scroll}</small></h4>
              <div style={{ display: "flex", gap: 10, overflowX: "auto", margin: "0 -16px", padding: "0 16px", scrollbarWidth: "none" }}>
                {tour.photos.map((ph, i) => (
                  <div key={i} style={{ flex: "none", width: i === 0 ? 250 : 200, height: 240, borderRadius: 22, overflow: "hidden", position: "relative", background: "#DDE6EC" }}>
                    <img src={photoSrc(ph)} alt={ph.caption || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    {ph.caption && <span style={{ position: "absolute", left: 10, bottom: 10, background: "rgba(255,255,255,.92)", borderRadius: 99, padding: "4px 10px", font: `700 11.5px ${fHead}` }}>{ph.caption}{ph.credit ? ` · ${ph.credit}` : ""}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* consiglio host */}
          {tour.host_tip && (
            <div style={block}>
              <div style={{ background: T.yellowSoft, borderRadius: 22, padding: 16, display: "grid", gridTemplateColumns: "44px 1fr", gap: 12, alignItems: "start" }}>
                <span style={{ width: 44, height: 44, borderRadius: "50%", background: T.ink, color: T.yellow, display: "grid", placeItems: "center", font: `900 18px ${fHead}` }}>{(hostName || "H").slice(0, 1).toUpperCase()}</span>
                <div><b style={{ font: `800 14px ${fHead}`, display: "block" }}>{t.tip} {hostName}</b><p style={{ margin: "2px 0 0", fontSize: 14.5 }}>{tour.host_tip}</p></div>
              </div>
            </div>
          )}

          {/* dove mangiare */}
          {(tour.pois || []).filter((p) => p.type === "mangiare").length > 0 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.eat} <small style={{ font: `600 12px ${fHead}`, color: T.muted }}>{t.zone}</small></h4>
              <div style={{ display: "grid", gap: 10 }}>
                {tour.pois.filter((p) => p.type === "mangiare").map((p, i) => (
                  <div key={i} style={{ background: "#fff", borderRadius: 22, padding: 12, display: "grid", gap: 8 }}>
                    <div><b style={{ font: `800 16px/1.15 ${fHead}`, display: "block" }}>{p.name}</b><small style={{ color: T.muted, fontSize: 12.5 }}>{p.note}</small></div>
                    {(p.maps_url || (p.lat && p.lng)) && <a href={p.maps_url || `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", borderRadius: 13, padding: 10, font: `700 14px ${fHead}`, background: T.ink, color: "#fff", display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}><MapPin size={15} style={{ color: T.yellow }} />{t.takeThere}</a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* cosa portare */}
          {(tour.bring || []).length > 0 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.bring}</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {tour.bring.map((b, i) => <span key={i} style={{ background: "#fff", borderRadius: 99, padding: "8px 12px", fontSize: 13 }}>{b}</span>)}
              </div>
            </div>
          )}

          {/* link utili */}
          {(tour.links || []).length > 0 && (
            <div style={block}>
              <h4 className="disp" style={h4}>{t.links}</h4>
              <div style={{ display: "grid", gap: 8 }}>
                {tour.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" style={{ background: "#fff", borderRadius: 16, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", font: `700 14px ${fHead}`, color: T.ink, textDecoration: "none" }}>
                    <span>{l.title}{l.subtitle && <small style={{ display: "block", font: `500 12px ${fHead}`, color: T.muted }}>{l.subtitle}</small>}</span>
                    <ArrowUpRight size={18} />
                  </a>
                ))}
              </div>
            </div>
          )}

          <p style={{ margin: "22px 16px 0", fontSize: 11.5, color: T.muted, textAlign: "center" }}>{t.aiNote} {hostName} · {t.updated} {now}</p>
        </div>

        {/* dock */}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "12px 14px 18px", background: `linear-gradient(transparent, ${T.cream} 30%)`, display: "grid", gridTemplateColumns: "1fr auto", gap: 8, zIndex: 6 }}>
          <a href={startMaps(tour)} target="_blank" rel="noopener noreferrer" data-testid="wb3-tour-start" style={{ textDecoration: "none", borderRadius: 16, padding: 15, font: `700 15px ${fHead}`, background: T.ink, color: "#fff", display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}><MapPin size={18} style={{ color: T.yellow }} />{t.takeMe}</a>
          <button onClick={() => { const url = window.location.href; if (navigator.share) navigator.share({ title: tour.title, url }); else navigator.clipboard?.writeText(url); }} aria-label={t.share} style={{ width: 54, borderRadius: 16, border: "none", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}><Share2 size={20} color={T.ink} /></button>
        </div>
      </div>
    </div>
  );
}

const retLabel = (mode, lang) => {
  const m = {
    it: { stessa_strada: "Ritorno per la stessa strada", anello: "Percorso ad anello", battello: "Ritorno in battello", mezzi_pubblici: "Ritorno con i mezzi pubblici" },
    en: { stessa_strada: "Return the same way", anello: "Loop route", battello: "Return by boat", mezzi_pubblici: "Return by public transport" },
    fr: { stessa_strada: "Retour par le même chemin", anello: "Boucle", battello: "Retour en bateau", mezzi_pubblici: "Retour en transports" },
    de: { stessa_strada: "Rückweg gleiche Strecke", anello: "Rundweg", battello: "Rückfahrt mit dem Schiff", mezzi_pubblici: "Rückfahrt mit Öffis" },
    es: { stessa_strada: "Vuelta por el mismo camino", anello: "Ruta circular", battello: "Vuelta en barco", mezzi_pubblici: "Vuelta en transporte" },
  };
  return (m[lang] || m.it)[mode] || mode;
};
